import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, filter, finalize, tap } from 'rxjs/operators';
import { CustomValidators } from 'src/app/shared/validators/custom-validators';
import { FormValidationComponent } from 'src/app/shared/validators/form-validation-component';
import { UserPublicV2Service } from '../services/user-public.service';
import { throwError } from 'rxjs';
import {
  CNPJ_MASK,
  CNPJ_SPECIAL_CHARACTERS,
  normalizeDocumentForSubmit,
} from 'src/app/_shared/utils/document-mask.util';

type PayloadToken = {
  accountId: number;
  cnpj: string;
  cpf: string;
  name: string;
  email: string;
  mobile: string;
};

@Component({
  selector: 'app-user',
  templateUrl: './user-register.component.html',
  styleUrls: ['./user-register.component.scss'],
})
export class UserRegisterComponent
  extends FormValidationComponent
  implements OnInit
{
  token: string;
  payload: PayloadToken = null;
  isForbiddenLink = false;
  isLoading = true;

  form: FormGroup;
  formLabels: Record<string, string> = {
    cpf: 'CPF',
    name: 'Nome',
    mobile: 'Celular',
    password: 'Senha',
    passwordConfirm: 'Senha',
  };

  hidePassword = true;
  hidePasswordConfirm = true;

  cnpjMask = CNPJ_MASK;
  cnpjSpecialCharacters = CNPJ_SPECIAL_CHARACTERS;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly fb: FormBuilder,
    private readonly userPublicV2Service: UserPublicV2Service,
    private readonly router: Router
  ) {
    super();
  }

  ngOnInit(): void {
    this.decodeToken();
    this.onValidateToken();

    this.form = this.fb.group({
      cpf: [this.payload.cpf, [Validators.required]],
      name: [this.payload.name, [Validators.required]],
      email: [this.payload.email, [Validators.required]],
      password: [null, [Validators.required, CustomValidators.password()]],
      passwordConfirm: [
        null,
        [Validators.required, CustomValidators.confirmation('password')],
      ],
    });

    this.listenPasswordStatusChanges();
  }

  onRegister() {
    const rawValue = this.form.getRawValue();
    rawValue.cpf = normalizeDocumentForSubmit(rawValue.cpf);

    this.userPublicV2Service.register(rawValue, this.token).subscribe(() => {
      this.router.navigate([`/login`]);
    });
  }

  private onValidateToken() {
    this.userPublicV2Service
      .validateToken(this.token)
      .pipe(
        catchError((error) => {
          this.isForbiddenLink = true;
          return throwError(() => error);
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((isValid) => {
        this.isForbiddenLink = !isValid;
      });
  }

  private decodeToken() {
    const token64 = this.route.snapshot.queryParamMap.get('token');

    if (!token64) {
      this.isForbiddenLink = true;
      return;
    }

    try {
      const decodedToken = this.base64ToUtf8(token64);
      this.token = decodedToken;

      const payloadBase64 = decodedToken.split('.')[1];
      const payloadJson = this.base64ToUtf8(payloadBase64);

      this.payload = JSON.parse(payloadJson);
    } catch (error) {
      this.isForbiddenLink = true;
      console.error('Erro ao decodificar token:', error);
    }
  }

  private base64ToUtf8(base64: string): string {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new TextDecoder('utf-8').decode(bytes);
  }

  private listenPasswordStatusChanges() {
    this.form
      .get('password')
      .statusChanges.pipe(
        filter((status) => status === 'VALID'),
        tap(() => {
          this.form.get('passwordConfirm').updateValueAndValidity();
        })
      )
      .subscribe();
  }
}
